import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { BillingLineItem } from '../../types/gst';

interface Props {
  lineItems: BillingLineItem[];
  computedItems: BillingLineItem[];
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onUpdateItem: (index: number, field: keyof BillingLineItem, val: any) => void;
}

export const LineItemEditor: React.FC<Props> = ({
  lineItems,
  computedItems,
  onAddItem,
  onRemoveItem,
  onUpdateItem
}) => {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-4">
      <div className="flex items-center justify-between border-b border-white/5 pb-3">
        <div className="text-xs font-bold uppercase text-cyan-400">
          Line Items & Tax Breakdown
        </div>
        <button
          type="button"
          onClick={onAddItem}
          className="flex items-center gap-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-2.5 py-1 text-xs font-semibold border border-cyan-500/30 transition"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add Item</span>
        </button>
      </div>

      <div className="space-y-4">
        {lineItems.map((item, idx) => (
          <div key={item.id} className="p-3.5 rounded-xl border border-white/5 bg-[#0e1015] space-y-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-400 text-[10px] uppercase">
                Item #{idx + 1}
              </span>
              {lineItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemoveItem(idx)}
                  className="text-slate-500 hover:text-rose-400 p-1"
                  title="Delete line item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-6">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">Description *</label>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => onUpdateItem(idx, 'description', e.target.value)}
                  placeholder="e.g. Consulting, Laptops, Software"
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-500"
                  required
                />
              </div>

              <div className="md:col-span-3">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">HSN/SAC Code</label>
                <input
                  type="text"
                  value={item.hsnSac}
                  onChange={(e) => onUpdateItem(idx, 'hsnSac', e.target.value)}
                  placeholder="e.g. 998313"
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="md:col-span-3">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">GST Slab %</label>
                <select
                  value={item.gstRate}
                  onChange={(e) => onUpdateItem(idx, 'gstRate', Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                >
                  <option value={0}>0% (Nil)</option>
                  <option value={5}>5%</option>
                  <option value={12}>12%</option>
                  <option value={18}>18% (Standard)</option>
                  <option value={28}>28% (Luxury)</option>
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">Qty</label>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => onUpdateItem(idx, 'quantity', Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="md:col-span-3">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">Unit Rate (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={item.unitPrice}
                  onChange={(e) => onUpdateItem(idx, 'unitPrice', Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="md:col-span-3">
                <label className="text-[10px] font-medium text-slate-400 block mb-1">Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={item.discountPercent}
                  onChange={(e) => onUpdateItem(idx, 'discountPercent', Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#131316] px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="md:col-span-3 text-right flex flex-col justify-end">
                <div className="text-[10px] text-slate-400">Total</div>
                <div className="font-bold text-white font-mono text-sm">
                  ₹{(computedItems[idx]?.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
